import {
  FC,
  useContext,
  useReducer,
  createContext,
  useMemo,
  useEffect,
  useCallback,
  ReactNode,
} from 'react'

import { useQueryParams, useDataLayer } from 'hooks'
import { generateTicketTitle, getIdFromSlug } from 'utils'
import { useQuery } from '@apollo/client'
import {
  GrantedPackagesQuery,
  GrantedPackagesQueryVariables,
  TevoTicketsQuery,
  TevoTicketsQueryVariables,
} from 'gql'
import { GRANTED_PACKAGES } from 'gql/platform/queries'
import { TEVO_TICKETS } from 'gql/artwork/queries'
import {
  QTY_ANY_FILTER_KEY,
  QTY_MORE_FILTER_KEY,
  QTY_MORE_FILTER_VALUE,
  SORT_PRICE_L_TO_H,
  qtyValueFromFilterAndSplit,
  calculateQtyValuesFromEventDetails,
} from './utils'
import { EventPageFiltersActionsState, EventPageFiltersState } from './types'

const State = createContext<EventPageFiltersState>({} as EventPageFiltersState)

const Actions = createContext<EventPageFiltersActionsState>({} as EventPageFiltersActionsState)

export const useStore = () => {
  const state = useContext(State)
  return state
}

export const useActions = () => {
  const actions = useContext(Actions)
  return actions
}

export const initialState = {
  // query state
  quantity: null,
  vip: false,
  sort: null,
  keywords: [],

  // NOTE: ideally, this state should be a part of event-page context,
  // but it has a lot in common with filters, so we need to store it here
  formQuantityOptions: [],
  formQuantity: null,
}

const SET_FORM_QUANTITY_OPTIONS = 'SET_FORM_QUANTITY_OPTIONS'
const SET_QUANTITY = 'SET_QUANTITY'
const SET_FORM_QUANTITY = 'SET_FORM_QUANTITY'
const SET_SORT = 'SET_SORT'
const SET_KEYWORDS = 'SET_KEYWORDS'

const TOGGLE_VIP = 'TOGGLE_VIP'

type ActionType =
  | typeof SET_FORM_QUANTITY_OPTIONS
  | typeof SET_QUANTITY
  | typeof SET_FORM_QUANTITY
  | typeof TOGGLE_VIP
  | typeof SET_SORT
  | typeof SET_KEYWORDS

const formStateReducer = (
  state = initialState,
  {
    data,
    type,
  }: {
    data?: Partial<typeof initialState>
    type: ActionType
  }
) => {
  switch (type) {
    case SET_FORM_QUANTITY_OPTIONS:
      return {
        ...state,
        formQuantityOptions: data.formQuantityOptions,
      }

    case SET_QUANTITY:
      return {
        ...state,
        quantity: data.quantity,
        formQuantity: data.formQuantity,
      }

    case SET_FORM_QUANTITY:
      return {
        ...state,
        formQuantity: data.formQuantity,
      }

    case TOGGLE_VIP:
      return {
        ...initialState,
        vip: !state.vip,
      }

    case SET_SORT:
      return {
        ...state,
        sort: data.sort,
      }

    case SET_KEYWORDS:
      return {
        ...state,
        keywords: data.keywords,
      }

    default:
      return state
  }
}

type Props = {
  children?: ReactNode
  fetchTEvoTickets?: boolean
}

const Provider: FC<Props> = ({ children, fetchTEvoTickets = true }) => {
  const {
    query: { slug, vip, quantity, sort, keywords },
    replace,
  } = useQueryParams()

  const dl = useDataLayer()

  const [state, dispatch] = useReducer(formStateReducer, {
    ...initialState,
    ...(quantity
      ? {
          quantity:
            quantity === QTY_ANY_FILTER_KEY || quantity === QTY_MORE_FILTER_KEY
              ? quantity
              : +quantity,
        }
      : {}),
    vip,
    sort: sort || SORT_PRICE_L_TO_H,
    keywords: keywords ? keywords.split(',') : [],
  })

  const tevoPackagesQuery = useQuery<TevoTicketsQuery, TevoTicketsQueryVariables>(TEVO_TICKETS, {
    variables: { artworkPk: parseInt(getIdFromSlug(slug), 10) },
    skip: vip || !fetchTEvoTickets,
  })

  const grantedPackagesQuery = useQuery<GrantedPackagesQuery, GrantedPackagesQueryVariables>(
    GRANTED_PACKAGES,
    {
      variables: { artworkSlug: slug, isTicket: false },
      skip: !vip,
    }
  )

  const globalQuantityOptions = useMemo(
    () =>
      calculateQtyValuesFromEventDetails({
        packageInfo: grantedPackagesQuery.data?.allPackages.edges.map(({ node }) => node),
        tevoTicketGroups: tevoPackagesQuery.data?.findArtwork.tevoTicketGroups,
      }),
    [grantedPackagesQuery.data, tevoPackagesQuery.data]
  )

  const dlEventItems = useMemo(() => {
    if (grantedPackagesQuery.data?.allPackages.edges.length) {
      return grantedPackagesQuery.data?.allPackages.edges.map(({ node }) => ({
        item_id: node.id,
        item_name: node.name,
      }))
    }

    if (tevoPackagesQuery.data?.findArtwork.tevoTicketGroups) {
      return tevoPackagesQuery.data?.findArtwork.tevoTicketGroups.map(({ id, row, section }) => ({
        item_id: id,
        item_name: generateTicketTitle(section, row),
      }))
    }

    return []
  }, [grantedPackagesQuery.data, tevoPackagesQuery.data])

  const setQuantity = useCallback(
    (qty: string | number) => {
      const numericQty = typeof qty === 'number' ? qty : Number(qty)

      dispatch({
        type: SET_QUANTITY,
        data: {
          quantity:
            // eslint-disable-next-line no-nested-ternary
            qty === QTY_ANY_FILTER_KEY || qty === QTY_MORE_FILTER_KEY
              ? qty
              : numericQty >= QTY_MORE_FILTER_VALUE
              ? QTY_MORE_FILTER_KEY
              : numericQty,
          formQuantity: qtyValueFromFilterAndSplit(qty, state.formQuantityOptions),
        },
      })
    },
    [state.formQuantityOptions]
  )

  const setSort = useCallback((s: string) => {
    dispatch({
      type: SET_SORT,
      data: {
        sort: s,
      },
    })
  }, [])

  const setFormQuantityOptions = useCallback(
    (formQuantityOptions: Array<number>) => {
      dispatch({
        type: SET_FORM_QUANTITY_OPTIONS,
        data: { formQuantityOptions },
      })

      dispatch({
        type: SET_FORM_QUANTITY,
        data: {
          formQuantity: qtyValueFromFilterAndSplit(state.quantity, formQuantityOptions),
        },
      })
    },
    [state.quantity]
  )

  const toggleVip = useCallback(() => {
    dl.push({
      event: 'view_item_list',
      ecommerce: {
        item_list_name: !state.vip ? 'packages' : 'tickets',
        item_list_id: `${getIdFromSlug(slug)}${!state.vip ? '-vip' : ''}`,
        items: dlEventItems,
      },
    })

    dispatch({
      type: TOGGLE_VIP,
    })
  }, [state.vip, dlEventItems])

  const setKeywords = useCallback((k: Array<string>) => {
    dispatch({
      type: SET_KEYWORDS,
      data: {
        keywords: k,
      },
    })
  }, [])

  useEffect(() => {
    replace(
      {
        query: {
          slug,
          ...(state.vip ? { vip: 'true' } : {}),
          quantity: state.quantity,
          sort: state.sort,
          ...(state.keywords.length ? { keywords: state.keywords.join(',') } : {}),
        },
      },
      undefined,
      {
        shallow: true,
      }
    )
  }, [state.quantity, state.vip, state.sort, state.keywords])

  const loading = useMemo(
    () => grantedPackagesQuery.loading || tevoPackagesQuery.loading,
    [grantedPackagesQuery.loading, tevoPackagesQuery.loading]
  )

  const stateValues = useMemo(
    () => ({ ...state, globalQuantityOptions, loading }),
    [state, globalQuantityOptions, loading]
  )
  const actionValues = useMemo(
    () => ({ setFormQuantityOptions, setQuantity, toggleVip, setSort, setKeywords }),
    [setFormQuantityOptions, setQuantity, toggleVip, setSort, setKeywords]
  )

  return (
    <State.Provider value={stateValues}>
      <Actions.Provider value={actionValues}>{children}</Actions.Provider>
    </State.Provider>
  )
}

export default Provider
